'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpCircle, ShoppingCart, Percent } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { ChartContainer } from './ChartContainer';
import { EmptyState } from './EmptyState';
import {
    useUpsellTakeRateTimeSeries,
    useUpsellTakeRateByOffer,
    useTopUpsellOffers,
    useUpsellTakeRateSummary,
    type UpsellTakeRateData,
} from '../hooks/metrics/useUpsellTakeRate';

interface UpsellTakeRateMetricsProps {
    data: {
        upsellTakeRateByDate?: Map<string, UpsellTakeRateData>;
    } | null | undefined;
    granularity: 'daily' | 'weekly' | 'monthly';
    loadingStates: {
        upsellTakeRate: boolean;
    };
    error: any;
}

export function UpsellTakeRateMetrics({ data, granularity, loadingStates, error }: UpsellTakeRateMetricsProps) {
    const { t } = useTranslation('analytics');
    const overallTimeSeriesData = useUpsellTakeRateTimeSeries(
        data?.upsellTakeRateByDate,
        granularity
    );

    // Aggregate summary from time series data
    const summary = useUpsellTakeRateSummary(data?.upsellTakeRateByDate);

    const offerComparisonData = useUpsellTakeRateByOffer(summary);
    const topOffers = useTopUpsellOffers(summary, 5);

    const isLoading = loadingStates.upsellTakeRate;

    if (error) {
        return (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricCard title={t('upsellTakeRate.title')} description={t('upsellTakeRate.errorDescription')} className="col-span-full">
                    <div className="flex items-center justify-center h-full">
                        <p className="text-destructive">{t('upsellTakeRate.errorMessage')}</p>
                    </div>
                </MetricCard>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Charts */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <MetricCard
                    title={t('upsellTakeRate.overallOverTime')}
                    description={t('upsellTakeRate.overallOverTimeDescription')}
                    isLoading={loadingStates.upsellTakeRate}
                >
                    <ChartContainer
                        data={overallTimeSeriesData}
                        chartType="line"
                        isPercentage={true}
                        emptyStateMessage={t('upsellTakeRate.noUpsellData')}
                    />
                </MetricCard>

                <MetricCard
                    title={t('upsellTakeRate.byPosition')}
                    description={t('upsellTakeRate.byPositionDescription')}
                    isLoading={loadingStates.upsellTakeRate}
                >
                    <ChartContainer
                        data={offerComparisonData}
                        chartType="bar"
                        isPercentage={true}
                        emptyStateMessage={t('upsellTakeRate.noPositionData')}
                    />
                </MetricCard>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <MetricCard
                    title={t('upsellTakeRate.funnelSummary')}
                    description={t('upsellTakeRate.funnelSummaryDescription')}
                    isLoading={isLoading}
                    dynamicHeight
                >
                    {!summary || topOffers.length === 0 ? (
                        <EmptyState message={t('upsellTakeRate.noFunnelData')} />
                    ) : (
                        <div className="space-y-4">
                            {/* Overall Take Rate - Prominent Display */}
                            <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg border border-primary/20">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-primary/20 rounded-lg">
                                        <Percent className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-muted-foreground">{t('upsellTakeRate.overallTakeRate')}</span>
                                        <p className="text-xs text-muted-foreground">
                                            {t('upsellTakeRate.ordersCount', { accepted: summary.mainOrdersWithUpsell.toLocaleString(), total: summary.totalMainOrders.toLocaleString() })}
                                        </p>
                                    </div>
                                </div>
                                <span className="text-2xl font-bold text-primary">{summary.overall.rate.toFixed(1)}%</span>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">{t('upsellTakeRate.totalMainOrders')}</span>
                                </div>
                                <span className="text-lg font-semibold">{summary.totalMainOrders.toLocaleString()}</span>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <ArrowUpCircle className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">{t('upsellTakeRate.ordersWithUpsell')}</span>
                                </div>
                                <span className="text-lg font-semibold">{summary.mainOrdersWithUpsell.toLocaleString()}</span>
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="text-sm font-medium text-muted-foreground mb-3">{t('upsellTakeRate.byPositionHeader')}</h4>
                                <div className="space-y-2">
                                    {topOffers.map(({ offerId, data: offerData }) => (
                                        <div key={offerId} className="flex items-center justify-between text-sm">
                                            <span>{offerData.offerTitle}</span>
                                            <div className="flex items-center gap-4">
                                                <span className="text-muted-foreground">
                                                    {offerData.accepted.toLocaleString()} / {offerData.total.toLocaleString()}
                                                </span>
                                                <span className="font-medium w-16 text-right">
                                                    {offerData.rate.toFixed(2)}%
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </MetricCard>

                <MetricCard
                    title={t('upsellTakeRate.understanding')}
                    description={t('upsellTakeRate.howCalculated')}
                    dynamicHeight
                >
                    <div className="space-y-4 text-sm text-muted-foreground">
                        <p>
                            <strong className="text-foreground">{t('upsellTakeRate.definitionBold')}</strong> {t('upsellTakeRate.definitionText')}
                        </p>

                        <div className="space-y-2">
                            <p><strong className="text-foreground">{t('upsellTakeRate.overallTakeRateLabel')}</strong></p>
                            <p className="pl-4">
                                {t('upsellTakeRate.overallTakeRateDescription')}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <p><strong className="text-foreground">{t('upsellTakeRate.positionTakeRateLabel')}</strong></p>
                            <p className="pl-4">
                                {t('upsellTakeRate.positionTakeRateFormula')}
                            </p>
                        </div>

                        <div className="p-3 bg-muted rounded-lg">
                            <p className="text-xs">
                                <strong>{t('upsellTakeRate.example')}</strong> {t('upsellTakeRate.exampleText')}
                            </p>
                        </div>
                    </div>
                </MetricCard>
            </div>
        </div>
    );
}
